import React from "react";
import { Link } from "@reach/router";

import ImagePlaceholder from "../ImagePlaceholder";
import { DESTINATIONS } from "../../data/exploreCommunities";

const html = (s) => ({ dangerouslySetInnerHTML: { __html: s } });

const GROUP_NOTES = {
  teens: {
    cls: "teens",
    label: "For your crew",
    text: "Fire Island is one of the best places to spend a day with a group — no cars, no fuss, everything in walking distance. The ferry is the whole vibe. Grab the upper deck on the way over.",
  },
  kids: {
    cls: "kids",
    label: "Great for families",
    text: "This community is a strong pick for families with kids — lifeguards on duty in season, easy beach access, and enough to keep everyone happy all day.",
  },
  solo: {
    cls: "solo",
    label: "Solo travel tip",
    text: "Fire Island is an easy solo destination — walkable, friendly, and genuinely hard to get lost on. The water taxi means you can always keep moving if the mood strikes.",
  },
};

const Result = ({ dest, winners, group, answers, needsParking, onRetake }) => {
  const d = DESTINATIONS[dest];
  const runners = winners.slice(1).filter((w) => DESTINATIONS[w]);
  const note = GROUP_NOTES[group];

  return (
    <div>
      <div className="result-hero">
        <ImagePlaceholder label={d.hero} rounded={false} src={d.heroImage} />
        <div className="hero-overlay" />
        <div className="hero-text">
          <div className="hero-eyebrow">Your perfect match</div>
          <div className="hero-title">{dest}</div>
          <div className="hero-tagline">{d.tagline}</div>
        </div>
      </div>

      <div className="amenity-row">
        {d.amenities.map((a) => (
          <div className="amenity-tag yes" key={a}>
            {a}
          </div>
        ))}
        {d.noAmenities.map((a) => (
          <div className="amenity-tag no" key={a}>
            {a}
          </div>
        ))}
      </div>
      <div className="divider" />

      {d.headsUp && (
        <div className="heads-up-box">
          <strong>{d.headsUp.title}</strong>
          {d.headsUp.text}
        </div>
      )}

      {note && (
        <div className={`group-note ${note.cls}`}>
          <strong>{note.label}</strong>
          {note.text}
        </div>
      )}

      <div className="section-title">About {dest}</div>
      <div className="desc">{d.desc}</div>

      <div className="insider-tips">
        <div className="insider-title">
          <i className="far fa-lightbulb" /> Local tips
        </div>
        {d.tips.slice(0, 3).map((t, i) => (
          <div className="qtip" key={t}>
            <span className="qtip-mark">{i + 1}</span>
            <span className="qtip-text">{t}</span>
          </div>
        ))}
      </div>

      <div className="captains-tip">
        <i className="far fa-anchor captain-icon" />
        <div>
          <div className="captain-label">Captain's Tip</div>
          <div className="captain-text" {...html(d.captainsTip)} />
        </div>
      </div>

      <div className="section-title">A sample day in {dest}</div>
      <div className="itinerary">
        {d.itinerary.map((s) => (
          <div className="itin-item" key={s.t + s.h}>
            <div className="itin-time">{s.t}</div>
            <div className="itin-content">
              <div className="itin-title">{s.h}</div>
              <div className="itin-detail">{s.d}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="divider" />
      <div className="section-title">Getting there</div>
      <div className="logistics-box">
        <div className="logistics-title">{d.logistics}</div>
        {d.logOpts.map((o) => (
          <div className="logistics-option" key={o.badge}>
            <div className={`lbadge${o.red ? " red" : ""}`}>{o.badge}</div>
            <div className="ldetail" {...html(o.text)} />
          </div>
        ))}
        {answers["3"] === "lirr" && (
          <div className="logistics-option">
            <div className="lbadge alt">LIRR note</div>
            <div
              className="ldetail"
              {...html(
                "Take the LIRR to <strong>Bay Shore, NY</strong>. Walk about a mile to the terminal, or catch <strong>David's Buses</strong> for a direct connection.",
              )}
            />
          </div>
        )}
        {needsParking && (
          <div className="logistics-option">
            <div className="lbadge alt">Parking</div>
            <div className="ldetail">
              Paid parking at the ferry terminal. Arrive early on summer
              weekends — lots fill up.
            </div>
          </div>
        )}
      </div>

      <div className="read-more">
        <div className="read-more-title">What others are saying</div>
        <div className="rm-links">
          {d.links.map((l) => (
            <a
              className="rm-link"
              href={l.url}
              target="_blank"
              rel="noopener noreferrer"
              key={l.url + l.title}
            >
              <div>
                <div className="rm-source">{l.src}</div>
                <div className="rm-title">{l.title}</div>
                <div className="rm-desc">{l.desc}</div>
              </div>
              <div className="rm-arrow">→</div>
            </a>
          ))}
        </div>
      </div>

      {runners.length > 0 && (
        <div className="runners">
          <div className="section-title">Also a great match for you</div>
          <div className="runner-list">
            {runners.map((rname) => {
              const r = DESTINATIONS[rname];
              return (
                <Link
                  className="runner-card"
                  to={`/communities/${r.slug}`}
                  key={rname}
                >
                  <ImagePlaceholder
                    label={r.hero}
                    className="runner-img"
                    rounded={false}
                    src={r.heroImage}
                  />
                  <div className="runner-body">
                    <div className="runner-name">{rname}</div>
                    <div className="runner-tagline">{r.tagline}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      )}

      <div className="read-more ps-block">
        <div className="read-more-title">P.S. — don't miss these</div>
        <div className="rm-links">
          <a
            className="rm-link"
            href="https://fireislandlighthouse.com/"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div>
              <div className="rm-source">
                Fire Island Lighthouse Preservation Society
              </div>
              <div className="rm-title">Fire Island Lighthouse</div>
              <div className="rm-desc">
                Climb 182 steps for panoramic views across the Great South Bay.
                Hours, tour times, and tickets on the official site.
              </div>
            </div>
            <div className="rm-arrow">→</div>
          </a>
          <a
            className="rm-link"
            href="https://www.nps.gov/places/the-sunken-forest-at-sailors-haven.htm"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div>
              <div className="rm-source">National Park Service</div>
              <div className="rm-title">The Sunken Forest at Sailors Haven</div>
              <div className="rm-desc">
                A 300-year-old maritime holly forest ranked “globally rare.”
                Reachable by ferry to Sailors Haven or by water taxi.
              </div>
            </div>
            <div className="rm-arrow">→</div>
          </a>
        </div>
      </div>

      <div className="cta-row">
        <Link className="cta-primary" to={`/schedules/${d.slug}`}>
          Book your ferry →
        </Link>
        <button type="button" className="cta-secondary" onClick={onRetake}>
          Not your vibe? Retake the quiz
        </button>
      </div>
    </div>
  );
};

export default Result;
